import usePersistentStore, { clearPersistedStateStorage } from '@gui/store/persistent/persistentStore';
import { PERSIST_STORE_VERSION, getCurrentBucketVersions } from '@gui/store/persistent/persistedBuckets';
import { LoadingStatus, useLoadingStore } from 'store/LoadingStore';
import { useErrorScreenStore } from '@gui/store/errorScreenStore';
import { requireApp } from 'store/assertions';
import { getEnvStore } from 'store/EnvStore';
import { loadPersistedStateIntoLocalStorage } from '@gui/store/persistent/loadPersistedState';

const AUTO_SAVE_INTERVAL_MS = 5 * 60_000;
const isObservationMode = (): boolean => Boolean(new URL(window.location.href).searchParams.get('observation_id'));

class StateController {
    private autoSaveIntervalId: number | null = null;

    constructor() {
        this.startAutoSave();
    }

    public destroy(): void {
        this.stopAutoSave();
    }

    private canAccessAutomaticStateApi(): boolean {
        if (isObservationMode()) return false;

        const { gridId } = requireApp();
        if (getEnvStore().env === 'local-offline' || getEnvStore().env === 'demo' || !gridId) return false;

        return true;
    }

    private canUploadAutomaticStateApi(): boolean {
        if (!this.canAccessAutomaticStateApi()) return false;

        const isErrorScreenVisible = useErrorScreenStore.getState().isErrorScreenVisible;
        if (isErrorScreenVisible) return false;

        const loadingStatus = useLoadingStore.getState().loadingStatus;
        if (loadingStatus === LoadingStatus.ERROR) return false;

        return true;
    }

    public async uploadAutomaticStateNow(): Promise<void> {
        try {
            if (!this.canUploadAutomaticStateApi()) return;

            const { apiClient, gridId } = requireApp();

            const { global, layers, designSchemes } = usePersistentStore.getState();
            await apiClient.put(`grids/${gridId}/states/automatic`, {
                version: PERSIST_STORE_VERSION,
                bucketVersions: getCurrentBucketVersions(),
                state: { global, layers, designSchemes },
            });
        } catch (error) {
            console.warn('Could not upload automatic state', error);
        }
    }

    private startAutoSave(): void {
        this.stopAutoSave();
        void this.uploadAutomaticStateNow();
        this.autoSaveIntervalId = window.setInterval(() => {
            void this.uploadAutomaticStateNow();
        }, AUTO_SAVE_INTERVAL_MS);
    }

    private stopAutoSave(): void {
        if (this.autoSaveIntervalId !== null) {
            window.clearInterval(this.autoSaveIntervalId);
            this.autoSaveIntervalId = null;
        }
    }

    // restores the most appropriate automatic save snapshot to the provided storage name.
    public async restoreAutoSaveState(storageName?: string): Promise<boolean> {
        if (!this.canAccessAutomaticStateApi()) {
            return false;
        }

        if (!storageName) {
            console.warn('Persistent storage name is not configured');
            return false;
        }

        const { apiClient, gridId } = requireApp();
        try {
            await apiClient.post(`grids/${gridId}/states/automatic/rollback`);
            const response = await apiClient.get(`grids/${gridId}/states/automatic/state`);
            loadPersistedStateIntoLocalStorage(response.data, storageName);
            return true;
        } catch (e) {
            console.warn('Could not restore automatic save state', e);
            return false;
        }
    }

    private async clearAutoSaveState(): Promise<void> {
        try {
            clearPersistedStateStorage();
        } catch (e) {
            console.warn('Could not clear automatic save state', e);
        }
    }

    public async deleteAutomaticState(): Promise<boolean> {
        if (!this.canAccessAutomaticStateApi()) {
            await this.clearAutoSaveState();
            return false;
        }

        const { apiClient, gridId } = requireApp();

        try {
            await apiClient.delete(`grids/${gridId}/states/automatic`);
            await this.clearAutoSaveState();
            return true;
        } catch (e) {
            console.warn('Could not delete automatic save state', e);
            await this.clearAutoSaveState();
            return false;
        }
    }
}

export default StateController;
